import { createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import { IApiError } from '@/models/apiError.model';
import { showApiErrors } from '@/utils';
import { getWebsites, createWebsite, updateWebsite, deleteWebsite, reorderWebsites } from './actionCreators';
import { SLICE_NAME, WebsitesState, initialState } from './models';

export const websitesSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    changePosition: (state: WebsitesState, action) => {
      state.websites = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWebsites.fulfilled, (state: WebsitesState, action) => {
        const { websites, page, pageSize, total } = action.payload;
        state.isLoading = false;
        state.websites = websites;
        state.pagination = { page, pageSize, total };
      })
      .addCase(createWebsite.fulfilled, (state: WebsitesState, action) => {
        const newWebsite = action.payload;

        if (state.pagination && state.pagination.page !== 1) {
          state.isLoading = false;
          return;
        }

        state.websites = [newWebsite, ...(state.websites ?? [])];
        if (state.pagination) {
          state.pagination.total += 1;
          if (state.websites.length > 10) {
            state.websites.pop();
          }
        }

        state.isLoading = false;
      })
      .addCase(updateWebsite.fulfilled, (state: WebsitesState, action) => {
        const { payload } = action;
        state.isLoading = false;
        state.websites = (state.websites ?? []).map((website) => (website.id === payload.id ? payload : website));
      })
      .addCase(deleteWebsite.fulfilled, (state: WebsitesState, action) => {
        const { payload } = action;
        state.isLoading = false;
        state.websites = (state.websites ?? []).filter((website) => website.id !== payload);
        if (state.pagination) {
          state.pagination.total -= 1;
        }
      })
      .addCase(reorderWebsites.fulfilled, (state: WebsitesState) => {
        state.isLoading = false;
        state.error = null;
      })
      .addMatcher(
        isPending(getWebsites, createWebsite, updateWebsite, deleteWebsite, reorderWebsites),
        (state: WebsitesState) => {
          state.isLoading = true;
          state.error = null;
        },
      )
      .addMatcher(
        isRejected(getWebsites, createWebsite, updateWebsite, deleteWebsite, reorderWebsites),
        (state: WebsitesState, action) => {
          const { error } = action;
          state.isLoading = false;
          state.error = error as IApiError;
          showApiErrors(error as IApiError);
        },
      );
  },
});

export const { changePosition } = websitesSlice.actions;
